import React from 'react';
import DataVisualizationComponent from '../../../Common/DataVisualizationComponent';
import { TileDefinition } from '../../../../types/dashboard';
import { TileSettings } from '../../../../types/tileBuilderTypes';
import { AnalyticsChartType } from '../../../../support/campaignAnalytics';

/**
 * Renders one campaign-analytics chart by reusing the dashboard tile renderer in its inline-data
 * mode (`suppressReduxInit` + `customData`), so a rollup chart is drawn by exactly the same code —
 * and looks identical to — a persisted dashboard `CustomTile` (see [[tile-renderer-inline-data]] and
 * the `WidgetRenderer` precedent). No backend query, no Redux writes: the flat `data` rows are fed
 * straight through the chart shaper.
 *
 * This is the single embedding seam for tiles in the Campaign Admin. It is intentionally decoupled
 * from the data-import feature (it does not depend on `WidgetSpec`) so analytics can evolve
 * independently. It must render inside the app's Redux `<Provider>` — the admin section already does
 * (`DataVisualizationComponent` reads, but never writes, the store in inline mode).
 */

/** Re-exported for callers that were importing the chart-type union from here. */
export type CampaignTileChartType = AnalyticsChartType;

type Props = {
  chartType: AnalyticsChartType;
  /** Flat row array. For charts: `[{ label, value }]`; for datagrid: raw row objects. */
  data: any[];
  /** Tile settings — e.g. `{ labelField, valueField }` for charts, `{ selectedColumns }` for tables. */
  settings?: Record<string, any>;
  /** Rendered height of the chart body. A number is pixels; `"100%"` stretches to the parent (fill mode). */
  height?: number | string;
  /** Stable, unique id for this tile so its synthetic `TileDefinition.id` never collides. */
  tileKey?: string | number;
};

/** Build a throwaway `TileDefinition` for inline rendering — negative id, no queries/transforms. */
function toTileDefinition(props: Props): TileDefinition {
  const raw = typeof props.tileKey === 'number' ? props.tileKey : hashKey(String(props.tileKey ?? props.chartType));
  const syntheticId = -1 - (Math.abs(raw) % 1_000_000);
  return {
    id: syntheticId,
    name: '',
    chart_type: props.chartType,
    queries: [],
    transformations: [],
    settings: (props.settings || {}) as TileSettings,
    category: null,
    layout: { i: String(syntheticId), x: 0, y: 0, w: 6, h: 6 },
  } as unknown as TileDefinition;
}

/** Tiny deterministic string hash so a `tileKey` string maps to a stable synthetic id. */
function hashKey(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return h;
}

const CampaignTileBase: React.FC<Props> = (props) => {
  const { data, settings, height = 300 } = props;
  const tileDefinition = React.useMemo(
    () => toTileDefinition(props),
    // Rebuild only when the identity-affecting inputs change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.chartType, props.tileKey, JSON.stringify(settings)]
  );

  return (
    <div style={{ width: '100%', height, minHeight: typeof height === 'number' ? height : 0 }}>
      <DataVisualizationComponent
        tileDefinition={tileDefinition}
        suppressReduxInit
        customData={data}
        queriesOverride={[]}
        transformationsOverride={[]}
        settingsOverride={settings}
        displayContext="preview"
      />
    </div>
  );
};

const CampaignTile = React.memo(CampaignTileBase);

export default CampaignTile;
